import {
  allocationPreferenceLabel,
  feedstockCategoryLabel,
  escaBonusCapLabel,
  buildFacilityValue,
  parseFacilityValue,
  FACILITY_KINDS,
  toAllocationPreferenceArray,
} from '@/config/formOptions';
import { formatDateInput, formatDateOrDash } from '@/lib/format/date';
import { formatQuantityOrDash } from '@/lib/format/number';
import { formatTextOrDash } from '@/lib/format/text';
import { getTranslation } from '@/lib/utils/i18n';
import {
  buildQuantitiesByUnit,
  buildRawQuantitiesByUnit,
  defaultUnitType,
} from '@/lib/mappers/quantities';
import { UNIT_TYPES } from '@/config/unitTypes.constants';
import { isEscaBonusCapOption } from '@/config/declarationOptions';
import { unitSymbol } from '@/lib/mappers/units';
import { formValue } from '@/lib/mappers/formValues';
import {
  buildDeclarationFiltersInput,
  processDiscrepancyItems,
  processFilterOptions,
} from '@/lib/mappers';
import { joinReferenceId } from '@/lib/utils/id';
import { fileStreamUrl } from '@/lib/utils/files';
import {
  declarationStatusLabel,
  verificationStatusLabel,
  shipmentStatusLabel,
} from '@/config/statuses';
import {
  DECLARATION_FILE_FIELD,
  CERTIFICATE_VERIFICATION,
  GHG_EMISSION_UNIT,
  DECLARATION_LINKED_SHIPMENT_SORT_FIELDS,
} from '../constants/declarations.constants';
import {
  isDeclarationLocked,
  toBackendYesNo,
  fromBackendYesNo,
  fromBackendBoolean,
} from '../utils/declarationsUtils';

// The server ORs DeclarationSearch's keys, so one box searches all four.
const buildDeclarationSearchInput = (search) => {
  const term = search?.trim();
  if (!term) return undefined;
  const text = { contains: term, mode: 'insensitive' };
  return {
    referenceId: text,
    declarationNo: text,
    sentBySupplier: { companyname: text },
    material: { name: text },
  };
};

export const buildDeclarationsQuery = ({
  certificateType,
  page,
  pageSize,
  search,
  extraFilters,
  ...filters
} = {}) => ({
  certificateType,
  page,
  limit: pageSize,
  search: buildDeclarationSearchInput(search),
  filters: buildDeclarationFiltersInput(filters, extraFilters),
});

// Unlike the REST row, `declarations` carries all three legs for surplus and write-off too.
const buildUnitsByType = (node) => ({
  [UNIT_TYPES.working]: {
    unit: node.workingUnit,
    amounts: {
      receivedQty: node.receivedQuantityWorking,
      adjustedQty: node.adjustedQuantityWorking,
      availableQty: node.availableQuantityWorking,
      surplusQty: node.surplusQuantityWorking,
      writeOffQty: node.writeOffQuantityWorking,
    },
  },
  [UNIT_TYPES.declaration]: {
    unit: node.declarationUnit,
    amounts: {
      receivedQty: node.receivedQuantityDeclaration,
      adjustedQty: node.adjustedQuantityDeclaration,
      availableQty: node.availableQuantityDeclaration,
      surplusQty: node.surplusQuantityDeclaration,
      writeOffQty: node.writeOffQuantityDeclaration,
    },
  },
  [UNIT_TYPES.tracking]: {
    unit: node.trackingUnit,
    amounts: {
      receivedQty: node.receivedQuantityTracking,
      adjustedQty: node.adjustedQuantityTracking,
      availableQty: node.availableQuantityTracking,
      surplusQty: node.surplusQuantityTracking,
      writeOffQty: node.writeOffQuantityTracking,
    },
  },
});

// The fields the row "⋯" menu gates on — shared by the list row and the detail header.
const buildMenuGateFields = (quantities) => ({
  // Mirrors the backend's own "already in use" gate on update/delete (InboundHelper).
  isInUse:
    (quantities.availableQuantityDeclaration ?? 0) < (quantities.adjustedQuantityDeclaration ?? 0),
  hasSurplusOrWriteOff:
    quantities.surplusQuantityDeclaration != null || quantities.writeOffQuantityDeclaration != null,
  rawSurplus: quantities.surplusQuantityDeclaration,
  rawWriteOff: quantities.writeOffQuantityDeclaration,
});

// Exactly one of the three is set — a supplier, another site, or a back-to-back account.
const sentByName = (node) =>
  node.sentBySupplier?.companyname ??
  node.sentBySite?.siteName ??
  node.sentByBackToBackAccount?.accountName;

// One `declarations` node → list view model.
export const processDeclaration = (node) => {
  const unitsByType = buildUnitsByType(node);
  const byUnit = buildQuantitiesByUnit(unitsByType);
  const declarationUnit = unitSymbol(node.declarationUnit);
  return {
    id: node.id,
    batchId: formatTextOrDash(node.referenceId),
    declarationNo: formatTextOrDash(node.declarationNo),
    materialName: formatTextOrDash(node.material?.name),
    supplierName: formatTextOrDash(sentByName(node)),
    status: declarationStatusLabel(node.status) ?? node.status,
    statusValue: node.status,
    // The Declarations column's View opens the PDF itself, not the row.
    declarationFileUrl: node.declarationFile?.id ? fileStreamUrl(node.declarationFile.id) : null,
    verificationStatus:
      verificationStatusLabel(node.declarationVerificationStatus) ??
      node.declarationVerificationStatus,
    verificationStatusValue: node.declarationVerificationStatus,
    shippingFrom: formatTextOrDash(node.shippingFrom),
    facilityName: formatTextOrDash(node.site?.siteName ?? node.backToBackAccount?.accountName),
    terminal: formatTextOrDash(node.terminal),
    vessel: formatTextOrDash(node.vessel),

    unitType: defaultUnitType(byUnit),
    byUnit,
    rawByUnit: buildRawQuantitiesByUnit(unitsByType),

    ...buildMenuGateFields(node),

    // The columns show these in the declaration unit; `byUnit` carries all three legs.
    surplusQty: formatQuantityOrDash(node.surplusQuantityDeclaration, declarationUnit),
    writeOffQty: formatQuantityOrDash(node.writeOffQuantityDeclaration, declarationUnit),

    ghgEmissions: formatTextOrDash(node.ghgEmissions),
    ghgSavings: formatTextOrDash(node.ghgEmissionSavings),
    rawMaterialType: formatTextOrDash(node.rawMaterialType),
    countryOfOrigin: formatTextOrDash(node.countryOfOrigin),
    feedstockCategory: formatTextOrDash(feedstockCategoryLabel(node.feedstockCategory)),
    allocationPreference: formatTextOrDash(
      toAllocationPreferenceArray(node.allocationPreference)
        .map(allocationPreferenceLabel)
        .join(', '),
    ),
    dateOfDischarge: formatDateOrDash(node.dateOfDischarge),
    dateOfDispatch: formatDateOrDash(node.dateOfDispatch),
    dateOfIssuance: formatDateOrDash(node.dateOfIssuance),
    discrepancyCount: node.discrepancyCount ?? 0,
    discrepancies: processDiscrepancyItems(node.discrepancies),
  };
};

export const processDeclarationFilterOptions = (result) => ({
  materialOptions: processFilterOptions(result?.materials),
  supplierOptions: processFilterOptions(result?.suppliers),
});

// A `DeclarationConnection` → rows + totalCount; `total` already respects search and filters.
export const processDeclarationsPage = (connection) => ({
  rows: (connection?.nodes ?? []).map(processDeclaration),
  totalCount: connection?.pageInfo?.total ?? 0,
});

// Exactly one of the three is set — a site, a back-to-back account, or a storage location.
const receivedAtName = (node) =>
  node?.receivedAtSite?.siteName ??
  node?.receivedAtBackToBackAccount?.accountName ??
  node?.receivedAtStorage?.storageName;

const formatPeriodRange = (period) =>
  `${formatDateOrDash(period?.startDate)} - ${formatDateOrDash(period?.endDate)}`;

// The header's "Mass balance period" reads `Period 1 (01/01/2026 to 31/03/2026)`.
const formatPeriodLabel = (period) =>
  period?.name ? `${period.name} (${formatPeriodRange(period)})` : '-';

const buildPeriodUnitsByType = (row) => ({
  [UNIT_TYPES.working]: {
    unit: row.workingUnit,
    amounts: {
      openingBalance: row.openingBalanceWorking,
      inboundQty: row.inboundQuantityWorking,
      surplusQty: row.surplusQuantityWorking,
      writeOffQty: row.writeOffQuantityWorking,
      allocatedQty: row.allocatedQuantityWorking,
      closingBalance: row.closingBalanceWorking,
    },
  },
  [UNIT_TYPES.declaration]: {
    unit: row.declarationUnit,
    amounts: {
      openingBalance: row.openingBalanceDeclaration,
      inboundQty: row.inboundQuantityDeclaration,
      surplusQty: row.surplusQuantityDeclaration,
      writeOffQty: row.writeOffQuantityDeclaration,
      allocatedQty: row.allocatedQuantityDeclaration,
      closingBalance: row.closingBalanceDeclaration,
    },
  },
  [UNIT_TYPES.tracking]: {
    unit: row.trackingUnit,
    amounts: {
      openingBalance: row.openingBalanceTracking,
      inboundQty: row.inboundQuantityTracking,
      surplusQty: row.surplusQuantityTracking,
      writeOffQty: row.writeOffQuantityTracking,
      allocatedQty: row.allocatedQuantityTracking,
      closingBalance: row.closingBalanceTracking,
    },
  },
});

const processMassBalancePeriodRow = (row, index) => {
  const byUnit = buildQuantitiesByUnit(buildPeriodUnitsByType(row));
  return {
    id: row.id,
    serialNo: index + 1,
    name: formatTextOrDash(row.name),
    period: formatPeriodRange(row),
    // The row's "Current" badge is keyed by the code, not the label.
    statusCode: row.status,
    unitType: defaultUnitType(byUnit),
    byUnit,
  };
};

/**
 * A column id → the `ShipmentOrderByField` that sorts it.
 *
 * Every other column the tab draws has no server-side sort; an unmapped field is dropped.
 */
export const buildLinkedShipmentOrderBy = (sort) => {
  const field = DECLARATION_LINKED_SHIPMENT_SORT_FIELDS[sort?.id];
  return field ? [{ field, direction: sort.dir ?? 'asc' }] : undefined;
};

const buildLinkedShipmentUnitsByType = (node) => {
  const shipment = node.shipment ?? {};
  return {
    [UNIT_TYPES.working]: {
      unit: shipment.workingUnit,
      amounts: {
        assignedQty: node.assignedQuantityWorking,
        actualShipmentQty: shipment.shipmentQuantityWorking,
        unassignedQty: shipment.unassignedContractQuantityWorking,
        pendingQty: shipment.pendingShipmentQuantityWorking,
        availableQty: shipment.availableShipmentQuantityWorking,
      },
    },
    [UNIT_TYPES.declaration]: {
      unit: shipment.declarationUnit,
      amounts: {
        assignedQty: node.assignedQuantityDeclaration,
        actualShipmentQty: shipment.shipmentQuantityDeclaration,
        unassignedQty: shipment.unassignedContractQuantityDeclaration,
        pendingQty: shipment.pendingShipmentQuantityDeclaration,
        availableQty: shipment.availableShipmentQuantityDeclaration,
      },
    },
    [UNIT_TYPES.tracking]: {
      unit: shipment.trackingUnit,
      amounts: {
        assignedQty: node.assignedQuantityTracking,
        actualShipmentQty: shipment.shipmentQuantityTracking,
        unassignedQty: shipment.unassignedContractQuantityTracking,
        pendingQty: shipment.pendingShipmentQuantityTracking,
        availableQty: shipment.availableShipmentQuantityTracking,
      },
    },
  };
};

const processLinkedShipmentForDeclaration = (node, index) => {
  const shipment = node.shipment ?? {};
  const byUnit = buildQuantitiesByUnit(buildLinkedShipmentUnitsByType(node));
  return {
    id: shipment.id,
    serialNo: index + 1,
    shipmentId: formatTextOrDash(shipment.shipmentId),
    linkedContract: formatTextOrDash((node.linkedContractIds ?? []).join(', ')),
    shipmentStatus: formatTextOrDash(shipmentStatusLabel(shipment.status)),
    terminal: formatTextOrDash(shipment.terminal),
    unitType: defaultUnitType(byUnit),
    byUnit,
    ghgEmissions: formatTextOrDash(shipment.ghgEmissions),
    ghgEmissionSavings: formatTextOrDash(shipment.ghgEmissionSavings),
    rawMaterialType: formatTextOrDash(shipment.rawMaterialType),
    countryOfOrigin: formatTextOrDash(shipment.countryOfOrigin),
    feedstockCategory: formatTextOrDash(feedstockCategoryLabel(shipment.feedstockCategory)),
    allocationPreference: formatTextOrDash(
      toAllocationPreferenceArray(shipment.preferredAllocation)
        .map(allocationPreferenceLabel)
        .join(', '),
    ),
    dateOfDispatch: formatDateOrDash(shipment.dateOfDispatch),
    discrepancies: shipment.discrepancyCount ?? 0,
    discrepancyItems: shipment.discrepancies ?? [],
  };
};

// The Linked incoming shipments tab — the rows are shipments, so every column is a shipment's.
export const processLinkedShipmentsForDeclaration = (connection) => ({
  rows: (connection?.nodes ?? []).map(processLinkedShipmentForDeclaration),
  totalCount: connection?.pageInfo?.total ?? 0,
});

// The Attachments tab — the declaration's own files plus its shipment's.
export const processDeclarationAttachments = (node) =>
  (node?.attachments?.nodes ?? []).map((attachment, index) => ({
    id: attachment.id,
    fileId: attachment.id,
    serialNo: index + 1,
    name: formatTextOrDash(attachment.filename),
    lastUpdatedOn: formatDateOrDash(attachment.updatedAt ?? attachment.createdAt),
  }));

const buildAllocationUnitsByType = (row) => ({
  [UNIT_TYPES.working]: {
    unit: row.allocatedQuantityWorking == null ? null : row.workingUnit,
    amounts: { allocatedQty: row.allocatedQuantityWorking },
  },
  [UNIT_TYPES.declaration]: {
    unit: row.allocatedQuantityDeclaration == null ? null : row.declarationUnit,
    amounts: { allocatedQty: row.allocatedQuantityDeclaration },
  },
  [UNIT_TYPES.tracking]: {
    unit: row.allocatedQuantityTracking == null ? null : row.trackingUnit,
    amounts: { allocatedQty: row.allocatedQuantityTracking },
  },
});

/**
 * One `allocationDetails` row → the Allocation details tab.
 *
 * `DeclarationAllocation` publishes no id, so the row index is the table's key.
 */
const processDeclarationAllocation = (units) => (row, index) => {
  const byUnit = buildQuantitiesByUnit(
    buildAllocationUnitsByType({
      allocatedQuantityWorking: row.allocatedQuantityWorking,
      allocatedQuantityDeclaration: row.allocatedQuantityDeclaration,
      allocatedQuantityTracking: row.allocatedQuantityTracking,
      workingUnit: units.workingUnit,
      declarationUnit: units.declarationUnit,
      trackingUnit: units.trackingUnit,
    }),
  );
  return {
    id: String(index),
    serialNo: index + 1,
    transactionId: formatTextOrDash(row.transactionId),
    transactionType: row.transactionType,
    soldTo: formatTextOrDash(row.soldTo?.companyname),
    massBalancePeriod: formatPeriodLabel(row.massBalancePeriod),
    allocatedQuota: formatTextOrDash(row.allocatedQuota),
    allocatedAt: formatDateOrDash(row.allocatedAt),
    unitType: defaultUnitType(byUnit),
    byUnit,
  };
};

/**
 * The Allocation details tab.
 *
 * The rows carry amounts but no units, so the declaration's own three name them.
 */
export const processDeclarationAllocations = (node) => {
  const units = node?.overview?.quantities ?? {};
  return (node?.allocationDetails ?? []).map(processDeclarationAllocation(units));
};

const withPercentOrDash = (value) => (value == null ? '-' : `${value} %`);

const withGhgUnitOrDash = (value) => (value == null ? '-' : `${value} ${GHG_EMISSION_UNIT}`);

/**
 * `EmissionDetails` → the GHG cards' formatted values.
 *
 * Shared by the Declaration details tab and the Emission calculations tab, which read the
 * same type from two different fields.
 */
// The server's yes/no enum, mapped through its vocabulary — never the raw code.
const YES_NO_LABEL_KEYS = Object.freeze({ yes: 'COMMON_YES', no: 'COMMON_NO' });

const yesNoOrDash = (value) => {
  const key = YES_NO_LABEL_KEYS[value];
  return key ? getTranslation(key) : '-';
};

const processEmissionDetails = (emission = {}) => ({
  isEditable: emission.isEditable ?? false,
  ghgEmissionRed3: yesNoOrDash(emission.ghgEmissionRed3),
  totalGhgEmissions: withGhgUnitOrDash(emission.totalGhgEmissions),
  // The eight components are null whenever the RED III default was applied.
  ghgEec: withGhgUnitOrDash(emission.ghgEec),
  ghgEl: withGhgUnitOrDash(emission.ghgEl),
  ghgEp: withGhgUnitOrDash(emission.ghgEp),
  ghgEtd: withGhgUnitOrDash(emission.ghgEtd),
  ghgEu: withGhgUnitOrDash(emission.ghgEu),
  ghgEsca: withGhgUnitOrDash(emission.ghgEsca),
  ghgEccs: withGhgUnitOrDash(emission.ghgEccs),
  ghgEccr: withGhgUnitOrDash(emission.ghgEccr),
  allocatedHeat: withGhgUnitOrDash(emission.allocatedHeat),
  allocatedElectricity: withGhgUnitOrDash(emission.allocatedElectricity),
  escaBonusCap: formatTextOrDash(escaBonusCapLabel(emission.escaBonusCap)),
  nuts2Area: formatTextOrDash(emission.nuts2Area),
  biofuelsForTransport: withPercentOrDash(emission.biofuelsForTransport),
  bioliquidsBiomassFuelsElectricity: withPercentOrDash(emission.bioliquidsBiomassFuelsElectricity),
  biomassFuelsElectricityOutermostRegions: withPercentOrDash(
    emission.biomassFuelsElectricityOutermostRegions,
  ),
  bioliquidsBiomassFuelsUsefulHeat: withPercentOrDash(emission.bioliquidsBiomassFuelsUsefulHeat),
  biomassFuelsUsefulHeatCoalSubstitution: withPercentOrDash(
    emission.biomassFuelsUsefulHeatCoalSubstitution,
  ),
  fssFoodSecurityStandard: yesNoOrDash(emission.fssFoodSecurityStandard),
  eudrDeforestationRegulation: yesNoOrDash(emission.eudrDeforestationRegulation),
  nonGmoFoodFeed: yesNoOrDash(emission.nonGmoFoodFeed),
  nonGmoTechnicalMarkets: yesNoOrDash(emission.nonGmoTechnicalMarkets),
});

// The components ship as strings to keep four decimals; the running total needs numbers.
const toNumberOrZero = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

// The Emission calculations tab — the same type, read from its own field.
export const processDeclarationEmissionCalculations = (node) => {
  const emission = node?.emissionCalculations ?? {};
  return {
    ...processEmissionDetails(emission),

    // The modal edits three of the eight and needs them unformatted to prefill.
    rawGhgEec: emission.ghgEec ?? '',
    rawGhgEp: emission.ghgEp ?? '',
    rawGhgEtd: emission.ghgEtd ?? '',
    rawTotalTransportationEmissions: emission.totalTransportationEmissions ?? null,

    // The five the SD fixes; the modal shows them in its running total, never as inputs.
    rawGhgEl: toNumberOrZero(emission.ghgEl),
    rawGhgEu: toNumberOrZero(emission.ghgEu),
    rawGhgEsca: toNumberOrZero(emission.ghgEsca),
    rawGhgEccs: toNumberOrZero(emission.ghgEccs),
    rawGhgEccr: toNumberOrZero(emission.ghgEccr),

    supplierId: node?.sentBySupplier?.id ? String(node.sentBySupplier.id) : '',
    receivedAtSiteId: node?.receivedAtSite?.id ? String(node.receivedAtSite.id) : '',
    receivedAtSiteName: formatTextOrDash(node?.receivedAtSite?.siteName),
  };
};

// PUT /:module/inbound/emissions/:id — the six fields the route validates, nothing else.
export const buildEmissionsDto = (values) => ({
  total_emission: values.totalEmissions,
  eec_emissions: values.ghgEec === '' ? undefined : values.ghgEec,
  ep_emissions: values.ghgEp === '' ? undefined : values.ghgEp,
  etd_emissions: values.ghgEtd === '' ? undefined : values.ghgEtd,
  calorific_value: values.calorificValue === '' ? undefined : values.calorificValue,
  transportation_route_id: values.routeId === '' ? undefined : values.routeId,
});

// One `declaration` node → the Declaration details tab's three cards.
export const processDeclarationDetailsTab = (node) => {
  const details = node?.declarationDetails ?? {};
  const sd = details.sdDetails ?? {};
  const material = details.materialDetails ?? {};
  const emission = details.emissionDetails ?? {};
  return {
    sdDetails: {
      declarationNo: formatTextOrDash(sd.declarationNo),
      dateOfIssuance: formatDateOrDash(sd.dateOfIssuance),
      dateOfDispatch: formatDateOrDash(sd.dateOfDispatch),
      supplierCertificateNumber: formatTextOrDash(sd.supplierCertificateNumber),
      verificationStatus: verificationStatusLabel(sd.declarationVerificationStatus),
      verificationStatusCode: sd.declarationVerificationStatus,
      note: formatTextOrDash(sd.note),
    },

    materialDetails: {
      typeOfProduct: formatTextOrDash(material.typeOfProduct),
      typeOfRawMaterial: formatTextOrDash(material.typeOfRawMaterial),
      additionalInformation: formatTextOrDash(material.additionalInformation),
      countryOfOrigin: formatTextOrDash(material.countryOfOrigin),
      quantity: formatTextOrDash(material.quantity),
      unit: formatTextOrDash(unitSymbol(material.unit)),
      trackingUnit: formatTextOrDash(unitSymbol(material.trackingUnit)),
      density: formatTextOrDash(material.density),
      // The density label names its two units, so the card needs both symbols.
      densityWeightUnit: unitSymbol(material.trackingUnit),
      densityVolumeUnit: unitSymbol(material.unit),
      euRedCompliantMaterial: yesNoOrDash(material.euRedCompliantMaterial),
      isccCompliantMaterial: yesNoOrDash(material.isccCompliantMaterial),
      chainOfCustodyOption: formatTextOrDash(material.chainOfCustodyOption),
      countryOfBiofuelProduction: formatTextOrDash(material.countryOfBiofuelProduction),
      startDateOfBiofuelProduction: formatDateOrDash(material.startDateOfBiofuelProduction),
      startDateOfBioliquidUse: formatDateOrDash(material.startDateOfBioliquidUse),
      rawMaterialSustainabilityCriteria: yesNoOrDash(material.rawMaterialSustainabilityCriteria),
      biomassIntermediateCrop: yesNoOrDash(material.biomassIntermediateCrop),
      biomassIlucRiskFeedstocks: yesNoOrDash(material.biomassIlucRiskFeedstocks),
      rawMaterialWasteDefinition: yesNoOrDash(material.rawMaterialWasteDefinition),
      wasteAnimalByProductPermitNumber: formatTextOrDash(material.wasteAnimalByProductPermitNumber),
      supportForProduction: yesNoOrDash(material.supportForProduction),
      supportNatureAndScheme: formatTextOrDash(material.supportNatureAndScheme),
    },

    emissionDetails: processEmissionDetails(emission),
  };
};

// One `declaration` node → the detail page's header + Overview tab.
export const processDeclarationOverview = (node) => {
  const quantities = node?.overview?.quantities ?? {};
  const sustainability = node?.overview?.sustainability ?? {};
  const unitsByType = buildUnitsByType(quantities);
  const byUnit = buildQuantitiesByUnit(unitsByType);
  return {
    id: node?.id,
    referenceId: formatTextOrDash(node?.referenceId),
    declarationNo: formatTextOrDash(node?.declarationNo),
    materialName: formatTextOrDash(node?.material?.name),
    supplierName: formatTextOrDash(sentByName(node ?? {})),
    facilityName: formatTextOrDash(receivedAtName(node)),
    terminal: formatTextOrDash(node?.terminal),
    status: declarationStatusLabel(node?.status) ?? '-',
    // The raw codes too: the header's badge tones are keyed by them, not by the labels.
    statusCode: node?.status,
    // The list row's name for the same code — buildRowMenuActions reads it.
    statusValue: node?.status,
    verificationStatus: verificationStatusLabel(node?.declarationVerificationStatus) ?? '-',
    verificationStatusCode: node?.declarationVerificationStatus,
    canFulfillOrder: node?.canFulfillOrder ?? false,
    massBalancePeriod: formatPeriodLabel(node?.massBalancePeriod),
    dateOfIssuance: formatDateOrDash(node?.dateOfIssuance),
    dateOfDispatch: formatDateOrDash(node?.dateOfDispatch),
    dateOfDischarge: formatDateOrDash(node?.dateOfDischarge),
    declarationFileUrl: node?.declarationFile?.id ? fileStreamUrl(node.declarationFile.id) : null,
    // The discrepancy card counts the linked shipments; the server publishes only their ids.
    linkedShipmentCount: (node?.shipmentIds ?? []).length,
    discrepancyCount: node?.discrepancyCount ?? 0,
    discrepancies: processDiscrepancyItems(node?.discrepancies),

    // One entry per unit the declaration can express; a leg the server could not resolve is absent.
    byUnit,
    unitType: defaultUnitType(byUnit),
    rawByUnit: buildRawQuantitiesByUnit(unitsByType),
    ...buildMenuGateFields(quantities),
    ghgEmissions: formatTextOrDash(sustainability.ghgEmissions),
    ghgEmissionSavings: formatTextOrDash(sustainability.ghgEmissionSavings),
    rawMaterialType: formatTextOrDash(sustainability.rawMaterialType),
    countryOfOrigin: formatTextOrDash(sustainability.countryOfOrigin),
    feedstockCategory: formatTextOrDash(feedstockCategoryLabel(sustainability.feedstockCategory)),
    allocationPreference: formatTextOrDash(
      toAllocationPreferenceArray(sustainability.allocationPreference)
        .map(allocationPreferenceLabel)
        .join(', '),
    ),

    massBalancePeriodRows: (node?.overview?.massBalancePeriodDetails ?? []).map(
      processMassBalancePeriodRow,
    ),
  };
};

// Clean files only; the server skips a bad one rather than failing the batch.
export const selectUploadableFiles = (files) => (files ?? []).filter((entry) => !entry.errorCode);

// Every clean file under the one repeated field name.
export const buildDeclarationsFormData = (files) => {
  const body = new FormData();
  selectUploadableFiles(files).forEach((entry) => body.append(DECLARATION_FILE_FIELD, entry.file));
  return body;
};

// Files the server skipped instead of creating; `message` is its own user-facing copy.
const processSkippedDeclarations = (dto) =>
  (dto?.output?.data?.skipped ?? []).map((entry) => ({
    fileName: entry.filename,
    reason: entry.reason,
    message: entry.message,
  }));

// The standalone upload's created rows — `createdDeclarations`.
export const processUploadedDeclarations = (dto) => ({
  created: (dto?.output?.data?.createdDeclarations ?? []).map((batch) => ({
    id: batch.id,
    referenceId: batch.batch_id,
  })),
  skipped: processSkippedDeclarations(dto),
});

// The shipment route's created rows — `declarations`.
export const processAddedShipmentDeclarations = (dto) => ({
  created: (dto?.output?.data?.declarations ?? []).map((declaration) => ({
    id: declaration.id,
    referenceId: declaration.referenceId,
  })),
  skipped: processSkippedDeclarations(dto),
});

// One `shipment(id)` node → the Add declaration meta bar view model.
export const processShipmentForAddDeclarations = (shipment) => {
  const declarationUnit = unitSymbol(shipment?.declarationUnit);

  return {
    id: shipment?.id,
    shipmentId: formatTextOrDash(shipment?.shipmentId),
    facilityName: formatTextOrDash(shipment?.receivedAtSite?.siteName),
    materialName: formatTextOrDash(shipment?.material?.name),
    supplierName: formatTextOrDash(shipment?.sentBySupplier?.companyname),
    multiSiteTransfer: shipment?.multiSiteTransfer,
    declarationUnit: formatTextOrDash(declarationUnit),
    shipmentQuantity: formatQuantityOrDash(shipment?.shipmentQuantityDeclaration, declarationUnit),
    availableQuantity: formatQuantityOrDash(
      shipment?.availableShipmentQuantityDeclaration,
      declarationUnit,
    ),
    pendingQuantity: formatQuantityOrDash(
      shipment?.pendingShipmentQuantityDeclaration,
      declarationUnit,
    ),
    dateOfDispatch: formatDateOrDash(shipment?.dateOfDispatch),
    totalContractsLinked: shipment?.totalContractsLinked ?? 0,
    totalDeclarationsLinked: shipment?.totalDeclarationsLinked ?? 0,
  };
};

// The facility dropdown's value — `receivedAt` is exactly one of a site or a b2b account.
const toFacilityId = (node) => {
  if (node?.receivedAtSite?.id != null) {
    return buildFacilityValue(FACILITY_KINDS.site, node.receivedAtSite.id);
  }
  if (node?.receivedAtBackToBackAccount?.id != null) {
    return buildFacilityValue(FACILITY_KINDS.backToBack, node.receivedAtBackToBackAccount.id);
  }
  return '';
};

// Exactly one of the three "sent by" relations is ever set — supplier wins, matching sentBy's own SITE-/B2B- parsing.
const toSentById = (inbound) => {
  if (inbound?.sentBySupplier?.id != null) return String(inbound.sentBySupplier.id);
  if (inbound?.sentBySite?.id != null) return `SITE-${inbound.sentBySite.id}`;
  if (inbound?.sentByBackToBackAccount?.id != null)
    return `B2B-${inbound.sentByBackToBackAccount.id}`;
  return '';
};

/**
 * <ValidateDeclarationForm> initial values, from `declaration(id)` — shared by Validate and
 * Edit. The three cards come back as their own tabs, so each keeps its own object here.
 */
export const toDeclarationFormValues = (node) => {
  const tab = node?.declarationDetails ?? {};
  const sd = tab.sdDetails ?? {};
  const material = tab.materialDetails ?? {};
  const emission = tab.emissionDetails ?? {};
  const sustainability = node?.overview?.sustainability ?? {};

  return {
    idPrefix: '',
    idNumber: formValue(node?.referenceId),
    facilityId: toFacilityId(node),
    // Null where the terminal was free text rather than a picked record.
    terminalId: formValue(node?.terminalRefId),
    materialId: formValue(node?.material?.id),
    multisiteTransfer: fromBackendBoolean(node?.multiSiteTransfer),
    supplierId: toSentById(node),
    dateOfDischarge: formatDateInput(node?.dateOfDischarge),
    vessel: '',
    feedstockCategory: formValue(sustainability.feedstockCategory),
    allocationPreference: toAllocationPreferenceArray(sustainability.allocationPreference),
    declarationNo: formValue(sd.declarationNo),
    dateOfIssuance: formatDateInput(sd.dateOfIssuance),
    dateOfDispatch: formatDateInput(sd.dateOfDispatch),
    supplierCertificateNumber: formValue(sd.supplierCertificateNumber),
    // Only the MANUAL flag is the user's; an auto VERIFIED/REJECTED is the server's own.
    markedAsVerified:
      sd.declarationVerificationStatus === CERTIFICATE_VERIFICATION.manuallyVerified,
    certificateVerificationStatus: sd.declarationVerificationStatus ?? '',
    // Set by <SupplierCertificateField> from the live ISCC directory lookup, never loaded.
    certificateLiveVerified: false,
    typeOfProduct: formValue(material.typeOfProduct),
    typeOfRawMaterial: formValue(material.typeOfRawMaterial),
    additionalInformation: formValue(material.additionalInformation),
    countryOfOrigin: formValue(material.countryOfOrigin),
    quantity: formValue(material.quantity),
    energyContent: formValue(material.energyContent),
    euRedCompliantMaterial: fromBackendYesNo(material.euRedCompliantMaterial),
    isccCompliantMaterial: fromBackendYesNo(material.isccCompliantMaterial),
    chainOfCustodyOption: formValue(material.chainOfCustodyOption),
    countryOfBiofuelProduction: formValue(material.countryOfBiofuelProduction),
    startDateOfBiofuelProduction: formatDateInput(material.startDateOfBiofuelProduction),
    startDateOfBioliquidUse: formatDateInput(material.startDateOfBioliquidUse),
    sustainabilityCriteriaCompliance: fromBackendYesNo(material.rawMaterialSustainabilityCriteria),
    intermediateCrop: fromBackendYesNo(material.biomassIntermediateCrop),
    lowIlucRiskFeedstock: fromBackendYesNo(material.biomassIlucRiskFeedstocks),
    wasteOrResidueDefinition: fromBackendYesNo(material.rawMaterialWasteDefinition),
    wasteOrByProductPermitNumber: formValue(material.wasteAnimalByProductPermitNumber),
    productionSupportReceived: fromBackendYesNo(material.supportForProduction),
    supportNatureAndScheme: formValue(material.supportNatureAndScheme),
    defaultGhgValueApplied: fromBackendYesNo(emission.ghgEmissionRed3),
    totalGhgEmissions: formValue(emission.totalGhgEmissions),
    eec: formValue(emission.ghgEec),
    el: formValue(emission.ghgEl),
    ep: formValue(emission.ghgEp),
    etd: formValue(emission.ghgEtd),
    eu: formValue(emission.ghgEu),
    escaValue: formValue(emission.ghgEsca),
    eccs: formValue(emission.ghgEccs),
    eccr: formValue(emission.ghgEccr),
    allocatedHeat: formValue(emission.allocatedHeat),
    allocatedElectricity: formValue(emission.allocatedElectricity),
    // Non-option values (OCR free text / legacy labels) load as empty.
    escaBonusCap: isEscaBonusCapOption(emission.escaBonusCap)
      ? formValue(emission.escaBonusCap)
      : '',
    nuts2Area: formValue(emission.nuts2Area),
    biofuelsForTransportSaving: formValue(emission.biofuelsForTransport),
    bioliquidsElectricitySaving: formValue(emission.bioliquidsBiomassFuelsElectricity),
    biomassOutermostRegionsSaving: formValue(emission.biomassFuelsElectricityOutermostRegions),
    bioliquidsHeatSaving: formValue(emission.bioliquidsBiomassFuelsUsefulHeat),
    biomassCoalSubstitutionSaving: formValue(emission.biomassFuelsUsefulHeatCoalSubstitution),
    foodSecurityStandard: formValue(emission.fssFoodSecurityStandard),
    euDeforestationRegulation: formValue(emission.eudrDeforestationRegulation),
    nonGmoFoodFeed: formValue(emission.nonGmoFoodFeed),
    nonGmoTechnicalMarkets: formValue(emission.nonGmoTechnicalMarkets),
  };
};

// One `declaration(id)` node → <DeclarationForm>: header fields + locked/values.
export const toDeclarationDetail = (node) => ({
  id: node?.id,
  batchId: formValue(node?.referenceId),
  // The raw enums: the form gates its close prompt on the status, not on a label.
  statusValue: node?.status,
  verificationStatusValue: node?.declarationVerificationStatus,
  fileName: formValue(node?.declarationFile?.filename),
  fileUrl: fileStreamUrl(node?.declarationFile?.id),
  locked: isDeclarationLocked(node),
  values: toDeclarationFormValues(node),
});

// The only stored/validated unit; String() guards materialId arriving as a number, not a string.
const declarationUnitFor = (materialId, materials) =>
  materials?.find((material) => material.id === String(materialId))?.declarationUnit || undefined;

// Same SITE-/B2B- prefix convention the backend's sentBy parsing already uses.
const toReceivedAt = (facility) =>
  facility.id
    ? `${facility.kind === FACILITY_KINDS.site ? 'SITE' : 'B2B'}-${facility.id}`
    : undefined;

// Shared by validate/edit — same body shape on the backend, minus markedAsVerified.
const buildDeclarationDto = (values, materials) => {
  const facility = parseFacilityValue(values.facilityId);
  return {
    referenceId: joinReferenceId(values) || undefined,
    materialId: values.materialId || undefined,
    receivedAt: toReceivedAt(facility),
    receivedAtSiteId: facility.kind === FACILITY_KINDS.site ? facility.id : undefined,
    backToBackAccountId: facility.kind === FACILITY_KINDS.backToBack ? facility.id : undefined,
    terminalId: values.terminalId || undefined,
    // TODO: the server pins multi_site_transfer to false, so this is sent but not stored yet.
    multiSiteTransfer: toBackendYesNo(values.multisiteTransfer),
    sentBy: values.supplierId ? String(values.supplierId) : undefined,
    vessel: values.vessel || undefined,
    dateOfDischarge: values.dateOfDischarge || undefined,
    feedstockCategory: values.feedstockCategory || undefined,
    preferredAllocation: values.allocationPreference?.length
      ? values.allocationPreference
      : undefined,
    sdNumber: values.declarationNo || undefined,
    issuanceDate: values.dateOfIssuance || undefined,
    dispatchDate: values.dateOfDispatch || undefined,
    supplierCertificateNumber: values.supplierCertificateNumber || undefined,
    typeOfProduct: values.typeOfProduct || undefined,
    typeOfRawMaterial: values.typeOfRawMaterial || undefined,
    additionalInformation: values.additionalInformation || undefined,
    countryOfOrigin: values.countryOfOrigin || undefined,
    certifiedQuantity: values.quantity || undefined,
    unit: declarationUnitFor(values.materialId, materials),
    energyContent: values.energyContent || undefined,
    euRedCompliantMaterial: toBackendYesNo(values.euRedCompliantMaterial),
    isccCompliantMaterial: toBackendYesNo(values.isccCompliantMaterial),
    chainOfCustodyOption: values.chainOfCustodyOption || undefined,
    countryOfBiofuelProduction: values.countryOfBiofuelProduction || undefined,
    startDateOfBiofuelProduction: values.startDateOfBiofuelProduction || undefined,
    startDateOfBioliquidUse: values.startDateOfBioliquidUse || undefined,
    rawMaterialSustainabilityCriteria: toBackendYesNo(values.sustainabilityCriteriaCompliance),
    biomassIntermediateCrop: toBackendYesNo(values.intermediateCrop),
    biomassIlucRiskFeedstocks: toBackendYesNo(values.lowIlucRiskFeedstock),
    rawMaterialWasteDefinitionRed3: toBackendYesNo(values.wasteOrResidueDefinition),
    wasteAnimalByProductPermitNumber: values.wasteOrByProductPermitNumber || undefined,
    supportForProduction: toBackendYesNo(values.productionSupportReceived),
    supportNatureAndScheme: values.supportNatureAndScheme || undefined,
    ghgEmissionRed3: toBackendYesNo(values.defaultGhgValueApplied),
    totalGhgEmissions: values.totalGhgEmissions || undefined,
    ghgEec: values.eec || undefined,
    ghgEl: values.el || undefined,
    ghgEp: values.ep || undefined,
    ghgEtd: values.etd || undefined,
    ghgEu: values.eu || undefined,
    ghgEsca: values.escaValue || undefined,
    ghgEccs: values.eccs || undefined,
    ghgEccr: values.eccr || undefined,
    ghgAllocatedHeat: values.allocatedHeat || undefined,
    ghgAllocatedElectricity: values.allocatedElectricity || undefined,
    escaBonusCap: values.escaBonusCap || undefined,
    nuts2Area: values.nuts2Area || undefined,
    biofuelsForTransport: values.biofuelsForTransportSaving || undefined,
    bioliquidsBiomassFuelsElectricity: values.bioliquidsElectricitySaving || undefined,
    biomassFuelsElectricityOutermostRegions: values.biomassOutermostRegionsSaving || undefined,
    bioliquidsBiomassFuelsUsefulHeat: values.bioliquidsHeatSaving || undefined,
    biomassFuelsUsefulHeatCoalSubstitution: values.biomassCoalSubstitutionSaving || undefined,
    fssFoodSecurityStandard: values.foodSecurityStandard || undefined,
    eudrDeforestationRegulation: values.euDeforestationRegulation || undefined,
    nonGmoFoodFeed: values.nonGmoFoodFeed || undefined,
    nonGmoTechnicalMarkets: values.nonGmoTechnicalMarkets || undefined,
    // TODO: no ghgEmissionSavings field on the form yet, so it is never sent (iscc-backend).
  };
};

/**
 * Verified is verified whichever route got there: the ISCC directory hit that
 * <SupplierCertificateField> found, or the user marking a miss by hand. The v2 save path
 * can only ever store MANUALLY_VERIFIED (resolveDeclarationVerificationStatus,
 * iscc-backend), so a directory verification lands under that status too.
 * TODO: a distinct flag for a directory verification, once the server can store one.
 */
const isVerified = (values) => Boolean(values.markedAsVerified || values.certificateLiveVerified);

// Omitted when unverified: a DRAFT has no prior status, so absent defaults to PENDING too.
export const toValidateDeclarationDto = (values, materials) => ({
  ...buildDeclarationDto(values, materials),
  markedAsVerified: isVerified(values) ? 'true' : undefined,
});

/**
 * Explicit false when unverified — unlike validate, a manual status exists to demote. An
 * auto VERIFIED omits the key: the server reads any value as manual and would downgrade the
 * certificate it verified itself.
 */
export const toEditDeclarationDto = (values, materials) => ({
  ...buildDeclarationDto(values, materials),
  markedAsVerified:
    values.certificateVerificationStatus === CERTIFICATE_VERIFICATION.verified
      ? undefined
      : isVerified(values)
        ? 'true'
        : 'false',
});
